import { followClassificationPin } from './classification-pin';
import {
  workerPayrollSetupStatus,
  type PayrollSetupGap,
  type WorkerPayrollSetupSalaryFlatConfig,
} from './worker-payroll-setup';
import { workerEmploymentMatchesPayRole } from './worker-pay-role-employment';

/**
 * Shared, pure assembler for the per-worker payroll-setup verdict.
 *
 * It encapsulates the effective-dated fact assembly that turns raw
 * classification / pay-profile / pay-role / salary rows into the
 * `workerPayrollSetupStatus` input, then returns a display-ready verdict.
 *
 * ONE source of truth for the assembly, so the pay-profile page and the
 * batch staff-list/detail readiness service can never drift from each other
 * (mirrors the reason `workerPayrollSetupStatus` is itself shared — the
 * verdict logic lives in exactly one place). Effective-date coverage +
 * readability decisions are made here; the raw rows are provided by the
 * caller (RLS-scoped on the page, service-role in the batch service).
 */

export type WorkerPayrollSetupVerdictState = 'complete' | 'incomplete' | 'unknown';

export interface WorkerPayrollSetupClassificationRow {
  id: string;
  classification_stream: string | null;
  classification_level: string | null;
  pay_point: string | null;
  employment_type: string | null;
  award_code: string | null;
  hourly_rate_override?: number | string | null;
  status?: string | null;
  effective_from?: string;
  effective_to?: string | null;
}

export interface WorkerPayrollSetupPayRoleRow {
  /** Exact canonical event resolver result for governed secondary paid-role context. */
  rolePricingVerified?: boolean;
  id: string;
  status: string | null;
  eligibility_status: string | null;
  staff_classification_history_id: string | null;
  staff_pay_profile_history_id?: string | null;
  /** ACTIVE/DRAFT/... of the linked tenant_pay_roles row (nested lookup pre-flattened). */
  tenant_pay_role_status: string | null;
  role_award_code?: string | null;
  role_classification_stream?: string | null;
  role_classification_level?: string | null;
  role_pay_point?: string | null;
  role_employment_type?: string | null;
  role_employee_category?: string | null;
  role_eba_agreement_id?: string | null;
  role_pay_rule_version_id?: string | null;
  role_ordinary_hours_profile_id?: string | null;
}

export interface WorkerPayrollSetupProfileRow {
  id?: string;
  status: string | null;
  effective_from: string;
  effective_to: string | null;
  staff_classification_history_id: string | null;
  employment_type: string | null;
  award_code?: string | null;
  employee_category?: string | null;
  eba_agreement_id?: string | null;
  pay_rule_version_id?: string | null;
  ordinary_hours_profile_id?: string | null;
}

export interface WorkerPayrollSetupCalcContext {
  employment_type?: string | null;
  classification_stream?: string | null;
  classification_level?: string | null;
  pay_point?: string | null;
}

export interface WorkerPayrollSetupFacts {
  /** Tenant-local effective date (YYYY-MM-DD) coverage is checked against. */
  effectiveDate: string;
  /** True once the salary/payment_method row is readable (service-role or granted). */
  salaryFactsReadable: boolean;
  /** Raw staff.payment_method (null → AWARD default when readable). */
  paymentMethod: string | null;
  annualisedSalary: number | string | null;
  /** Current staff hourly rate for the governed CONTRACT_HOURLY path. */
  staffHourlyRate?: number | string | null;
  payPeriodsPerYear: number | null;
  salaryPayFrequency: string | null;
  /** Caller-computed (SALARY_FLAT path only) — null when not applicable/unknown. */
  salaryFlatReviewRequired: boolean | null;
  staffEmploymentType: string | null;
  staffAwardCode: string | null;
  staffEmployeeCategory?: string | null;
  needsAwardReview?: boolean | null;
  /** Whether classification rows below are readable for the caller. */
  canReadClassification: boolean;
  /** ACTIVE staff_classification_history rows covering effectiveDate. */
  classificationRows: WorkerPayrollSetupClassificationRow[];
  /** Whether pay-role assignment rows below are readable for the caller. */
  canReadPayRoleAssignments: boolean;
  /** ACTIVE staff_pay_role_assignments covering effectiveDate (tenant_pay_role_status flattened). */
  payRoleAssignments: WorkerPayrollSetupPayRoleRow[];
  /** staff_pay_profile_history rows (assembler filters to ACTIVE covering date). */
  profiles: WorkerPayrollSetupProfileRow[];
  /** Optional SECDEF calc-context enrichment (page only; batch passes null). */
  calcContext?: WorkerPayrollSetupCalcContext | null;
  calcContextReadable?: boolean;
}

export interface WorkerPayrollSetupVerdict {
  state: WorkerPayrollSetupVerdictState;
  gaps: PayrollSetupGap[];
}

function coversDate(effectiveDate: string, from: string, to: string | null | undefined): boolean {
  return from <= effectiveDate && (to == null || to === '' || to > effectiveDate);
}

function roleStatusUpper(value: string | null | undefined): string | null {
  return value != null ? String(value).toUpperCase() : null;
}

/**
 * Assemble the payroll-setup verdict for one worker on one date.
 * Behaviour mirrors the pay-profile page's inline assembly exactly.
 */
export function assembleWorkerPayrollSetup(
  facts: WorkerPayrollSetupFacts,
): WorkerPayrollSetupVerdict {
  const {
    effectiveDate,
    salaryFactsReadable,
    paymentMethod,
    annualisedSalary,
    payPeriodsPerYear,
    salaryPayFrequency,
    salaryFlatReviewRequired,
    staffEmploymentType,
    staffAwardCode,
    canReadClassification,
    classificationRows,
    canReadPayRoleAssignments,
    payRoleAssignments,
    profiles,
    calcContext = null,
    calcContextReadable = false,
  } = facts;

  // Legacy resolution requires one profile. Pay-role resolution may instead
  // use each assignment's explicit profile pin, so evaluate every assignment
  // rather than whichever row happens to arrive first.
  const activeProfilesCoveringDate = profiles.filter(
    (profile) =>
      roleStatusUpper(profile.status) === 'ACTIVE' &&
      coversDate(effectiveDate, profile.effective_from, profile.effective_to),
  );
  const singleActiveProfile =
    activeProfilesCoveringDate.length === 1 ? activeProfilesCoveringDate[0]! : null;

  const classifications = canReadClassification
    ? classificationRows.filter(
        (row) =>
          (row.status == null || ['ACTIVE', 'SUPERSEDED'].includes(roleStatusUpper(row.status)!)) &&
          (row.effective_from == null ||
            coversDate(effectiveDate, row.effective_from, row.effective_to)),
      )
    : [];
  const assignments = canReadPayRoleAssignments ? payRoleAssignments : [];
  const hasActivePayRoleAssignment = canReadPayRoleAssignments && assignments.length > 0;

  const assignedPayRoleIsActive = hasActivePayRoleAssignment
    ? assignments.every((row) => roleStatusUpper(row.tenant_pay_role_status) === 'ACTIVE')
    : null;
  const assignmentEvidence = assignments.map((assignment) => {
    const matchingProfiles = assignment.staff_pay_profile_history_id
      ? activeProfilesCoveringDate.filter(
          (profile) => profile.id === assignment.staff_pay_profile_history_id,
        )
      : activeProfilesCoveringDate.filter(
          (profile) =>
            profile.award_code === assignment.role_award_code &&
            (assignment.role_eba_agreement_id == null ||
              profile.eba_agreement_id === assignment.role_eba_agreement_id) &&
            (assignment.role_pay_rule_version_id == null ||
              profile.pay_rule_version_id === assignment.role_pay_rule_version_id),
        );
    const profile = matchingProfiles.length === 1 ? matchingProfiles[0]! : null;
    const explicitClassificationId =
      assignment.staff_classification_history_id ??
      profile?.staff_classification_history_id ??
      null;
    const pinnedClassification = explicitClassificationId
      ? followClassificationPin(explicitClassificationId, classificationRows, classifications)
      : null;
    const matchingClassifications = explicitClassificationId
      ? pinnedClassification
        ? [pinnedClassification]
        : []
      : classifications.filter(
          (row) =>
            row.award_code === assignment.role_award_code &&
            row.classification_stream === assignment.role_classification_stream &&
            row.classification_level === assignment.role_classification_level &&
            (assignment.role_pay_point == null || row.pay_point === assignment.role_pay_point) &&
            workerEmploymentMatchesPayRole(row.employment_type, assignment.role_employment_type),
        );
    const classification =
      matchingClassifications.length === 1 ? matchingClassifications[0]! : null;
    const category = roleStatusUpper(facts.staffEmployeeCategory);
    const roleCategory = roleStatusUpper(assignment.role_employee_category);
    const profileCategory = roleStatusUpper(profile?.employee_category);
    const rate = Number(classification?.hourly_rate_override ?? Number.NaN);
    const compatible =
      assignment.rolePricingVerified === true ||
      (roleStatusUpper(assignment.status) === 'ACTIVE' &&
        roleStatusUpper(assignment.eligibility_status) === 'ELIGIBLE' &&
        roleStatusUpper(assignment.tenant_pay_role_status) === 'ACTIVE' &&
        profile != null &&
        classification != null &&
        classification.award_code === assignment.role_award_code &&
        classification.classification_stream === assignment.role_classification_stream &&
        classification.classification_level === assignment.role_classification_level &&
        (assignment.role_pay_point == null ||
          classification.pay_point === assignment.role_pay_point) &&
        workerEmploymentMatchesPayRole(
          classification.employment_type,
          assignment.role_employment_type,
        ) &&
        profile.award_code === assignment.role_award_code &&
        profile.employment_type === assignment.role_employment_type &&
        profileCategory === roleCategory &&
        category === roleCategory &&
        (assignment.role_eba_agreement_id == null ||
          profile.eba_agreement_id === assignment.role_eba_agreement_id) &&
        (assignment.role_pay_rule_version_id == null ||
          profile.pay_rule_version_id === assignment.role_pay_rule_version_id) &&
        (assignment.role_ordinary_hours_profile_id == null ||
          profile.ordinary_hours_profile_id === assignment.role_ordinary_hours_profile_id) &&
        Number.isFinite(rate) &&
        rate > 0);
    return { profile, classification, compatible };
  });
  const hasActiveEligiblePayRoleAssignment =
    assignmentEvidence.length > 0 && assignmentEvidence.every((item) => item.compatible);

  // Deterministic display classification only; the verdict is the conjunction
  // across every assignment. Prefer L4 so its historical-input warning cannot
  // disappear when database row order changes.
  const linkedClassifications = assignmentEvidence
    .map((item) => item.classification)
    .filter((row): row is WorkerPayrollSetupClassificationRow => row != null)
    .sort((left, right) => left.id.localeCompare(right.id));
  const classification = hasActivePayRoleAssignment
    ? (linkedClassifications.find((row) => row.classification_level === 'L4') ??
      linkedClassifications[0] ??
      null)
    : singleActiveProfile?.staff_classification_history_id
      ? followClassificationPin(
          singleActiveProfile.staff_classification_history_id,
          classificationRows,
          classifications,
        )
      : null;
  let classificationLinkedToActiveProfile: boolean | null = canReadClassification
    ? hasActivePayRoleAssignment
      ? assignmentEvidence.every((item) => item.profile != null && item.classification != null)
      : classification != null
    : null;

  const classificationStream =
    classification?.classification_stream ??
    (calcContextReadable ? (calcContext?.classification_stream ?? null) : null);
  const classificationLevel =
    classification?.classification_level ??
    (calcContextReadable ? (calcContext?.classification_level ?? null) : null);
  const payPoint =
    classification?.pay_point ?? (calcContextReadable ? (calcContext?.pay_point ?? null) : null);

  if (
    classification == null &&
    calcContextReadable &&
    (classificationStream || classificationLevel) &&
    classificationLinkedToActiveProfile !== true
  ) {
    classificationLinkedToActiveProfile = null;
  }

  const employmentTypeForSetup =
    staffEmploymentType ??
    (calcContextReadable ? (calcContext?.employment_type ?? null) : null) ??
    classification?.employment_type ??
    singleActiveProfile?.employment_type ??
    null;

  const assignmentRoleTupleCompatible = hasActivePayRoleAssignment
    ? assignmentEvidence.every((item) => item.compatible)
    : null;
  const hasRequiredProfiles = hasActivePayRoleAssignment
    ? assignmentEvidence.every((item) => item.profile != null)
    : singleActiveProfile != null;

  const paymentMethodNormalised =
    typeof paymentMethod === 'string' && paymentMethod.trim().length > 0
      ? paymentMethod.trim().toUpperCase()
      : salaryFactsReadable
        ? 'AWARD'
        : null;

  if (!salaryFactsReadable) {
    return { state: 'unknown', gaps: [] };
  }

  if (
    paymentMethodNormalised === 'AGENCY_INVOICE' ||
    paymentMethodNormalised === 'VOLUNTEER' ||
    paymentMethodNormalised === 'CONTRACT_HOURLY'
  ) {
    const status = workerPayrollSetupStatus({
      paymentMethod: paymentMethodNormalised,
      employmentType: employmentTypeForSetup,
      agreedHourlyRate:
        paymentMethodNormalised === 'CONTRACT_HOURLY' ? facts.staffHourlyRate : null,
      hasActivePayProfile: hasRequiredProfiles,
      hasActivePayRoleAssignment: false,
      hasActiveEligiblePayRoleAssignment: false,
    });
    return { state: status.complete ? 'complete' : 'incomplete', gaps: status.gaps };
  }

  if (paymentMethodNormalised === 'SALARY_FLAT') {
    const salaryFlat: WorkerPayrollSetupSalaryFlatConfig = {
      annualisedSalary,
      payPeriodsPerYear,
      salaryPayFrequency,
    };
    const status = workerPayrollSetupStatus({
      paymentMethod: 'SALARY_FLAT',
      employmentType: employmentTypeForSetup,
      hasActivePayProfile: hasRequiredProfiles,
      hasActivePayRoleAssignment: false,
      hasActiveEligiblePayRoleAssignment: false,
      salaryFlat,
      salaryFlatReviewRequired,
    });
    return { state: status.complete ? 'complete' : 'incomplete', gaps: status.gaps };
  }

  // Award path — need classification + pay-role readability, else UNKNOWN.
  if (!canReadClassification || !canReadPayRoleAssignments) {
    return { state: 'unknown', gaps: [] };
  }

  const status = workerPayrollSetupStatus({
    paymentMethod: paymentMethodNormalised ?? 'AWARD',
    employmentType: employmentTypeForSetup,
    awardCode: staffAwardCode,
    classificationStream,
    classificationLevel,
    payPoint,
    agreedHourlyRate: hasActivePayRoleAssignment
      ? assignmentEvidence.every((item) => {
          const value = Number(item.classification?.hourly_rate_override ?? Number.NaN);
          return Number.isFinite(value) && value > 0;
        })
        ? 1
        : null
      : (classification?.hourly_rate_override ?? null),
    employeeCategory: facts.staffEmployeeCategory,
    needsAwardReview: facts.needsAwardReview,
    payRoleLinksCompatible: assignmentRoleTupleCompatible,
    hasActivePayProfile: hasRequiredProfiles,
    classificationLinkedToActiveProfile,
    hasActivePayRoleAssignment,
    hasActiveEligiblePayRoleAssignment,
    assignedPayRoleIsActive,
  });
  return { state: status.complete ? 'complete' : 'incomplete', gaps: status.gaps };
}
